import { formatLessonLabel } from './lessonTitle.js';
import { getCourseDisplayName, getPipelineType, PIPELINE_TYPE } from '../services/courseService.js';
import { getProjectConfig, getWholeUnitLabel } from '../data/projectsConfig.js';

/** @deprecated Use getPipelineType(topic) === PIPELINE_TYPE.NCE instead */
export const NCE_TOPIC_SET = new Set(['nce1', 'nce2', 'nce3', 'nce4']);
const isNce = (topic) => getPipelineType(topic) === PIPELINE_TYPE.NCE;
const REVIEW_SCHEDULE = [0, 1, 3, 6, 14, 29, 59, 89, 139];

/**
 * All 18 pipeline positions from 词汇 (ri=-9) through D140 (ri=8).
 * Each entry: { ri, stage, label }
 */
export const PIPELINE_POSITIONS = [
  { ri: -9, stage: 'vocab',     label: '词汇' },
  { ri: -8, stage: 'sentences', label: '句子 1' },
  { ri: -7, stage: 'sentences', label: '句子 2' },
  { ri: -6, stage: 'sentences', label: '句子 3' },
  { ri: -5, stage: 'paragraph', label: '整课读 1' },
  { ri: -4, stage: 'paragraph', label: '整课读 2' },
  { ri: -3, stage: 'paragraph', label: '整课读 3' },
  { ri: -2, stage: 'paragraph', label: '整课读 4' },
  { ri: -1, stage: 'paragraph', label: '整课读 5' },
  { ri: 0,  stage: 'memorize',  label: 'D1' },
  { ri: 1,  stage: 'memorize',  label: 'D2' },
  { ri: 2,  stage: 'memorize',  label: 'D4' },
  { ri: 3,  stage: 'memorize',  label: 'D7' },
  { ri: 4,  stage: 'memorize',  label: 'D15' },
  { ri: 5,  stage: 'memorize',  label: 'D30' },
  { ri: 6,  stage: 'memorize',  label: 'D60' },
  { ri: 7,  stage: 'memorize',  label: 'D90' },
  { ri: 8,  stage: 'memorize',  label: 'D140' },
];

/**
 * Get a position-specific label for a review_index.
 * For phonics and storybook courses, ri>=0 uses "复习" instead of "背诵".
 */
export function getPositionLabel(ri, topic) {
  const pos = PIPELINE_POSITIONS.find(p => p.ri === ri);
  if (!pos) return `ri=${ri}`;
  if (ri >= 0 && !isNce(topic)) {
    return `复习 ${pos.label}`;
  }
  return ri >= 0 ? `${pos.label}` : pos.label;
}

/**
 * Compute stage flags for any review_index.
 * Returns { vocab_enabled, sentences_enabled, recite_enabled, combined_mode, memorize }
 *
 * NCE: standard pipeline (vocab→sentences→paragraph→memorize)
 * Phonics: all ri>=0 positions are review (no memorize)
 * Storybook: ri 0-3 = vocab+sentences, ri 4+ = recite+combined, never memorize
 */
export function getStageFlagsForRI(ri, topic) {
  if (ri === -9) return { vocab_enabled: 1, sentences_enabled: 0, recite_enabled: 0, combined_mode: 0, memorize: 0 };
  if (ri >= -8 && ri <= -6) return { vocab_enabled: 0, sentences_enabled: 1, recite_enabled: 0, combined_mode: 0, memorize: 0 };
  if (ri >= -5 && ri <= -1) return { vocab_enabled: 0, sentences_enabled: 0, recite_enabled: 1, combined_mode: 1, memorize: 0 };
  // ri >= 0: depends on course type
  if (isNce(topic)) {
    return { vocab_enabled: 0, sentences_enabled: 0, recite_enabled: 1, combined_mode: 1, memorize: 1 };
  }
  if (getPipelineType(topic) === PIPELINE_TYPE.PHONICS) {
    return { vocab_enabled: 0, sentences_enabled: 0, recite_enabled: 1, combined_mode: 0, memorize: 0 };
  }
  // Storybook courses: ri 0-3 = vocab+sentences, ri 4+ = recite+combined
  if (ri <= 3) {
    return { vocab_enabled: 1, sentences_enabled: 1, recite_enabled: 0, combined_mode: 0, memorize: 0 };
  }
  return { vocab_enabled: 0, sentences_enabled: 0, recite_enabled: 1, combined_mode: 1, memorize: 0 };
}

/**
 * ============================================================
 * SINGLE SOURCE OF TRUTH: src/shared/assignmentTypes.json
 *
 * All assignment type definitions (flags, labels, colors, descriptions)
 * live in that JSON file. Everything below is DERIVED from it.
 * To add a new type, edit assignmentTypes.json — nothing else.
 * ============================================================
 */
import typeDefs from '../shared/assignmentTypes.json';

const ALL_TYPES = [...typeDefs.assignmentTypes, ...typeDefs.systemTypes];

/** The 8 flag-based assignment types (vocab, sentences, paragraph, etc.) */
export const ASSIGNMENT_TYPES = typeDefs.assignmentTypes;

/** Backward-compatible alias: each entry has { value, label, flags } */
export const SINGLE_TYPES = typeDefs.assignmentTypes.map(t => ({ value: t.key, label: t.label, flags: t.flags }));

/** All stage type keys including system types (review, extra, quiz, project) */
export const STAGE_TYPE_KEYS = ALL_TYPES.map(t => t.key);

/** Maps stage type key → display label */
export const STAGE_TYPE_LABELS = Object.fromEntries(ALL_TYPES.map(t => [t.key, t.label]));

/** Maps stage type key → CSS color index (color-0 through color-11) */
export const STAGE_TYPE_COLOR_MAP = Object.fromEntries(ALL_TYPES.map(t => [t.key, t.colorIndex]));

/** Maps stage type key → Chinese description */
export const STAGE_TYPE_DESCRIPTIONS = Object.fromEntries(ALL_TYPES.map(t => [t.key, t.description]));

/** Ordered hex colors for legend rendering (index must match colorIndex) */
export const STAGE_TYPE_COLORS = (() => {
  const arr = [];
  for (const t of ALL_TYPES) arr[t.colorIndex] = t.color;
  return arr;
})();

/**
 * Reverse-map an assignment's boolean flags to the matching type key.
 * Returns the key string (e.g. 'vocab', 'vocab-sentences') or null if no match.
 */
export function flagsToStageType(a) {
  return ASSIGNMENT_TYPES.find(st =>
    !!a.vocab_enabled === st.flags.vocab_enabled &&
    !!a.sentences_enabled === st.flags.sentences_enabled &&
    !!a.recite_enabled === st.flags.recite_enabled &&
    !!a.combined_mode === st.flags.combined_mode &&
    !!a.combined_vocab === st.flags.combined_vocab &&
    !!a.memorize === st.flags.memorize
  )?.key ?? null;
}

/**
 * Get the stage type key for an assignment, used for color-coding.
 * Quiz/project types are checked first, then extras use flag matching,
 * then pipeline stages use review_index mapping.
 */
export function getStageTypeKey(assignment) {
  if (assignment.assignment_type === 'quiz') return 'quiz';
  if (assignment.assignment_type === 'project') return 'project';
  if ((assignment.assignment_type === 'extra' || assignment.assignment_type === 'learn') && (assignment.review_index ?? 0) < 0) {
    // Match on distinguishing flags (v/s/r/m + combined_vocab) — combined_mode is an implementation detail
    const v = !!assignment.vocab_enabled;
    const s = !!assignment.sentences_enabled;
    const r = !!assignment.recite_enabled;
    const m = !!assignment.memorize;
    const cv = !!assignment.combined_vocab;
    const match = ASSIGNMENT_TYPES.find(t =>
      v === t.flags.vocab_enabled && s === t.flags.sentences_enabled &&
      r === t.flags.recite_enabled && m === t.flags.memorize &&
      cv === (t.flags.combined_vocab || false)
    );
    return match?.key ?? 'extra';
  }
  const ri = assignment.review_index ?? 0;
  if (ri === -9) return 'vocab';
  if (ri >= -8 && ri <= -6) return 'sentences';
  if (ri >= -5 && ri <= -1) return 'paragraph';
  if (!isNce(assignment.topic)) return flagsToStageType(assignment) ?? 'review';
  return 'memorize';
}

/**
 * Returns true only for failure/manual extras (assignment_type === 'extra')
 * with a negative review_index.
 */
export function isFailureExtra(assignment) {
  return assignment.assignment_type === 'extra' && (assignment.review_index ?? 0) < 0;
}

/**
 * Returns true for auto-generated extras (from rating fail or smart review).
 * Manual extras and legacy (null) are NOT considered auto-generated.
 * NULL means "not applicable" (non-extra assignments) or "pre-column legacy extra".
 */
export function isAutoGeneratedExtra(extraSource) {
  return extraSource === 'auto' || extraSource === 'smart_review';
}

/**
 * Derive a label from the assignment's actual enabled flags.
 * Looks up the matching ASSIGNMENT_TYPE; falls back to '补充'.
 */
function getFlagBasedLabel(assignment) {
  const match = ASSIGNMENT_TYPES.find(t =>
    !!assignment.vocab_enabled === t.flags.vocab_enabled &&
    !!assignment.sentences_enabled === t.flags.sentences_enabled &&
    !!assignment.recite_enabled === t.flags.recite_enabled &&
    !!assignment.memorize === t.flags.memorize &&
    !!assignment.combined_vocab === (t.flags.combined_vocab || false)
  );
  return match?.label ?? '补充';
}

/**
 * Map an assignment to a compact stage code.
 * Accepts a full assignment object so extras get flag-based labels
 * (词+句, 背诵, etc.) instead of a generic "E".
 * Pipeline/normal assignments keep ri-based labels (句子, 整课读, 背诵 D7).
 */
export function getStageCode(assignment) {
  const ri = assignment.review_index ?? 0;
  const type = assignment.assignment_type;
  const topic = assignment.topic;

  if ((type === 'extra' || type === 'learn') && ri < 0) return getFlagBasedLabel(assignment);
  if (ri === -9) return '词汇';
  if (ri >= -8 && ri <= -6) return '句子';
  if (ri >= -5 && ri <= -1) return '整课读';
  const matchedKey = !isNce(topic) ? flagsToStageType(assignment) : null;
  const label = isNce(topic) ? '背诵' : (matchedKey ? STAGE_TYPE_LABELS[matchedKey] : '复习');
  if (ri >= 0 && ri <= 8) return `${label} D${REVIEW_SCHEDULE[ri] + 1}`;
  return `${label} D${ri}`;
}

/**
 * Extract NCE book number from topic string.
 * nce1→1, nce2→2, nce3→3, nce4→4
 */
function getBookNumber(topic) {
  const match = topic.match(/nce(\d)/);
  return match ? match[1] : '';
}

export function toRoman(num) {
  const numerals = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
  return numerals[num] || num;
}

/**
 * Get the batch/book number for a pill superscript.
 * NCE: book number from topic name (nce1 → '1').
 * Others: extract number from batch field (b2 → '2').
 */
export function getBatchNumber(assignment) {
  if (isNce(assignment.topic)) return getBookNumber(assignment.topic);
  const match = assignment.batch?.match(/\d+/);
  return match ? match[0] : '';
}

/**
 * Get a short topic label for pill display.
 * NCE: strip trailing digits ("新概念1" → "新概念") since the Roman numeral already encodes the book.
 * Others: use full displayName ("自然拼读", "歌曲", etc.)
 */
function getShortTopicLabel(topic) {
  const name = getCourseDisplayName(topic);
  if (isNce(topic)) return name.replace(/\d+$/, '');
  return name;
}

/**
 * Format an assignment into a compact label like "新概念 II. 第15课 背诵 D7"
 * Prepends a short topic label so calendar pills, Gantt rows, and CompletionTable
 * show the course name at a glance.
 * For phonics/songs, uses phonicsLetterLookup to show
 * the letter/pattern (e.g., "自然拼读 Aa 背诵 D1") instead of "第1课".
 */
export function formatAssignmentLabelParts(assignment, phonicsLetterLookup) {
  // A project row has lesson_number=0 and no stage — name its kind instead of "第0课 复习 D1".
  const isProject = !!assignment.project_id;
  const stageCode = isProject ? '' : getStageCode(assignment);
  const lessonLabel = isProject
    ? (getProjectConfig(assignment.project_id)?.wholeBook ? getWholeUnitLabel(assignment.topic) : '综合练习')
    : formatLessonLabel(assignment.topic, assignment.batch, assignment.lesson_number, phonicsLetterLookup);
  const bookNum = getBatchNumber(assignment);
  const topicPrefix = getShortTopicLabel(assignment.topic);
  const lesson = bookNum
    ? `${topicPrefix} ${toRoman(Number(bookNum))}. ${lessonLabel}`
    : `${topicPrefix} ${lessonLabel}`;
  return { lesson, stage: stageCode };
}

export function formatAssignmentLabel(assignment, phonicsLetterLookup) {
  const { lesson, stage } = formatAssignmentLabelParts(assignment, phonicsLetterLookup);
  return stage ? `${lesson} ${stage}` : lesson;
}
